import React from "react";
import { Box, Typography, useTheme, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useRenderPerformance } from "@/components/utils/performance";
import { cloudinaryService } from "@/helpers";
import { ProductCardProps } from "./ProductCard.types";

/**
 * Product Card Component - Figma Design Implementation
 */
export const ProductCard: React.FC<ProductCardProps> = React.memo(
  ({ product, onClick, onAddToCart, loading = false }) => {
    useRenderPerformance("ProductCard");

    const theme = useTheme();

    const productImage = product.images?.[0]?.url || null;
    const productPrice = product.pricePoint || 0;

    // State for image optimization
    const [optimizedImage, setOptimizedImage] = React.useState<{
      url: string;
      styles: null;
      isCloudinary?: boolean;
      fallbackUsed?: boolean;
    } | null>(null);

    const [imageLoading, setImageLoading] = React.useState<boolean>(false);

    const handleClick = React.useCallback(() => {
      if (onClick && !loading && !imageLoading) {
        onClick(product);
      }
    }, [onClick, loading, imageLoading, product]);

    const handleAddToCart = React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onAddToCart && !loading && !imageLoading) {
          onAddToCart(product);
        }
      },
      [onAddToCart, loading, imageLoading, product]
    );

    React.useEffect(() => {
      if (!productImage) {
        setOptimizedImage(null);
        setImageLoading(false);
        return;
      }

      let isCancelled = false; // Flag to prevent race conditions
      setImageLoading(true);

      // Use smart transformation with automatic fallback
      cloudinaryService
        .smartTransformImage(productImage, {
          width: 200,
          height: 200,
          crop: "fill",
          gravity: "auto",
        })
        .then((result) => {
          if (isCancelled) return; // Ignore if component unmounted or image changed

          setOptimizedImage({
            url: result.url,
            styles: null,
            isCloudinary: result.isCloudinary,
            fallbackUsed: result.fallbackUsed,
          });

          // Log fallback usage for debugging
          if (result.fallbackUsed) {
            console.info(
              "ProductCard: Using fallback URL for image:",
              productImage
            );
          }
        })
        .catch((error) => {
          if (isCancelled) return; // Ignore if component unmounted or image changed

          console.error("ProductCard: Image optimization failed:", error);
          // Fallback to original image
          setOptimizedImage({
            url: productImage,
            styles: null,
            isCloudinary: false,
            fallbackUsed: true,
          });
        })
        .finally(() => {
          if (!isCancelled) {
            setImageLoading(false);
          }
        });

      // Cleanup function to prevent race conditions
      return () => {
        isCancelled = true;
      };
    }, [productImage]);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          alignItems: "flex-start",
          justifyContent: "flex-start",
          width: "100%",
          height: "100%",
          cursor: onClick ? "pointer" : "default",
        }}
        onClick={handleClick}
      >
        <Box
          sx={{
            width: "100%",
            aspectRatio: "1",
            backgroundImage: optimizedImage?.url
              ? `url(${optimizedImage.url})`
              : productImage
              ? `url(${productImage})`
              : `url(/placeholder-dish.jpg)`,
            backgroundPosition: "50% 50%",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            borderRadius: theme.spacing(2), // 16px
            flexShrink: 0,
            backgroundColor: theme.palette.grey[200], // Fallback background
            // Combined loading state styling
            opacity: loading || imageLoading ? 0.7 : 1,
            transition: theme.transitions.create("opacity", {
              duration: theme.transitions.duration.short,
            }),
            // Add subtle loading indicator
            position: "relative",
            overflow: "hidden",
            "&::after": imageLoading
              ? {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: theme.spacing(2),
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                  animation: "shimmer 1.5s infinite",
                }
              : {},
            "@keyframes shimmer": {
              "0%": { transform: "translateX(-100%)" },
              "100%": { transform: "translateX(100%)" },
            },
          }}
        />

        {/* Product Content Container */}
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          {/* Product Details */}
          <Box flex={1}>
            <Typography
              component="h3"
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                color: theme.palette.text.primary,
              }}
            >
              {product.name}
            </Typography>

            {product.description && (
              <Typography fontSize="12px" color="text.secondary">
                {product.description}
              </Typography>
            )}
          </Box>

          <Button
            endIcon={<Add sx={{ color: "text.secondary" }} />}
            onClick={handleAddToCart}
            sx={{
              fontSize: 16,
              mt: 1,
              fontWeight: 600,
              backgroundColor: "action.focus",
              color: "text.primary",
              borderRadius: theme.spacing(1.25),
              padding: theme.spacing(0.5, 2),
            }}
          >
            {`${productPrice} ₽`}
          </Button>
        </Box>
      </Box>
    );
  }
);

ProductCard.displayName = "ProductCard";
